/* La Thé Box — home e-shop : les deux bascules de la page.
   Passation : _design/boutique/01-home-eshop/ §6 et §7

   Deux états locaux, aucun appel réseau : la vue du rayon (famille ou envie)
   et l'opération affichée dans « Les temps forts ». Le brief n'en demande pas
   d'autre, et la page fonctionne sans ce fichier — les `<noscript>` des deux
   sections déplient ce qui était masqué.

   Chaque section est traitée séparément : deux blocs « temps forts » sur la
   même page ne doivent pas se commander l'un l'autre. */

/* -------- « Trouvez votre thé » : par famille ou par envie -------- */
(() => {
  document.querySelectorAll('.ltb-es-rayons').forEach((section) => {
    const boutons = [...section.querySelectorAll('[data-vue]')];
    const vues = [...section.querySelectorAll('[data-ltb-vue]')];
    const aide = section.querySelector('[data-ltb-aide]');
    if (boutons.length < 2 || vues.length < 2) return;

    const montrer = (cle) => {
      boutons.forEach((b) => {
        const actif = b.dataset.vue === cle;
        b.classList.toggle('is-active', actif);
        b.setAttribute('aria-selected', actif ? 'true' : 'false');
      });
      vues.forEach((v) => {
        v.hidden = v.dataset.ltbVue !== cle;
      });
      // La ligne d'aide est unique et change de texte : deux paragraphes dont
      // l'un est masqué feraient sauter la page de leur différence de hauteur.
      if (aide) {
        const texte = aide.dataset[cle === 'envie' ? 'aideEnvie' : 'aideFamille'];
        if (texte) aide.textContent = texte;
      }
    };

    boutons.forEach((b) => b.addEventListener('click', () => montrer(b.dataset.vue)));

    /* Le méga-menu pointe ici : `#par-envie` et `#par-famille` ouvrent la
       bonne vue en plus de faire défiler. Le navigateur a déjà défilé quand ce
       script s'exécute — on ne refait donc pas son travail, on bascule
       seulement. `hashchange` couvre le cas d'un second clic sur le même lien
       depuis la page elle-même. */
    const surAncre = () => {
      const cle = { '#par-envie': 'envie', '#par-famille': 'famille' }[location.hash];
      if (cle) montrer(cle);
    };
    surAncre();
    window.addEventListener('hashchange', surAncre);
  });
})();

/* -------- « Les temps forts » : une opération à la fois -------- */
(() => {
  document.querySelectorAll('.ltb-es-tf').forEach((section) => {
    const onglets = [...section.querySelectorAll('[data-tf]')];
    const vues = [...section.querySelectorAll('[data-ltb-tf-vue]')];
    if (onglets.length < 2 || vues.length < 2) return;

    const montrer = (cle) => {
      onglets.forEach((o) => {
        const actif = o.dataset.tf === cle;
        o.classList.toggle('is-active', actif);
        o.setAttribute('aria-selected', actif ? 'true' : 'false');
      });
      vues.forEach((v) => {
        v.hidden = v.dataset.ltbTfVue !== cle;
      });
    };

    onglets.forEach((o) => o.addEventListener('click', () => montrer(o.dataset.tf)));

    // Les flèches parcourent les onglets, comme le veut le motif ARIA d'un
    // `tablist` : sans cela, le clavier ne sort pas du premier onglet.
    section.querySelector('[role="tablist"]')?.addEventListener('keydown', (e) => {
      const pas = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (!pas) return;
      const i = onglets.indexOf(document.activeElement);
      if (i < 0) return;
      e.preventDefault();
      const suivant = onglets[(i + pas + onglets.length) % onglets.length];
      suivant.focus();
      montrer(suivant.dataset.tf);
    });
  });
})();

/* -------- Le rail de rayons, épinglé en haut de l'écran mobile -------- */
(() => {
  const zone = document.querySelector('[data-ltb-rail-zone]');
  const rail = zone && zone.querySelector('.ltb-es-rail');
  if (!rail) return;

  // `position: sticky` ne convient pas : il s'arrête au bas du bloc conteneur,
  // c'est-à-dire au bas de la section d'en-tête, alors que les six ancres
  // visées vivent dans les sections suivantes.
  const mobile = window.matchMedia('(max-width: 749px)');
  let epingle = false;
  let attente = null;

  const relever = () => {
    attente = null;

    if (!mobile.matches) {
      if (epingle) {
        rail.classList.remove('is-pinned');
        zone.style.height = '';
        epingle = false;
      }
      return;
    }

    // Le seuil vient de la feuille : quand l'en-tête du site deviendra collant,
    // il suffira de poser `--ltb-es-entete` pour que le rail se range dessous.
    const brut = getComputedStyle(rail).getPropertyValue('--ltb-es-entete');
    const seuil = parseFloat(brut) || 0;

    if (!epingle && zone.getBoundingClientRect().top <= seuil) {
      zone.style.height = `${rail.offsetHeight}px`;
      rail.classList.add('is-pinned');
      epingle = true;
    } else if (epingle && zone.getBoundingClientRect().top > seuil) {
      rail.classList.remove('is-pinned');
      zone.style.height = '';
      epingle = false;
    }
  };

  const planifier = () => {
    if (attente) return;
    attente = requestAnimationFrame(relever);
  };

  window.addEventListener('scroll', planifier, { passive: true });
  window.addEventListener('resize', planifier);
  relever();
})();

/* -------- Le rail suit la lecture -------- */
/* Le rayon en cours porte le filet plein. C'est ce qui achève de dire ce qu'est
   ce rail : un sommaire de la page, pas une barre de filtres — et surtout pas
   les onglets des temps forts, qui lui ressemblaient trait pour trait en mobile
   et qui, eux, échangent du contenu.

   Un écouteur de défilement étranglé par `requestAnimationFrame`, et non un
   IntersectionObserver : le repère cherché est « la dernière section passée
   sous le rail », qui se lit d'un coup sur les positions, alors qu'un
   observateur ne signale que les franchissements et rend ses entrées dans le
   désordre. Même raisonnement que la barre de la page maison.

   Sans JavaScript, les six liens marchent : ils ne se marquent pas, voilà tout. */
(() => {
  const rail = document.querySelector('.ltb-es-rail');
  if (!rail) return;

  const liens = [...rail.querySelectorAll('.ltb-es-rail__lien')]
    .map((a) => {
      const cible = a.getAttribute('href') || '';
      if (!cible.startsWith('#') || cible.length < 2) return null;
      const el = document.getElementById(cible.slice(1));
      return el ? { a, el } : null;
    })
    .filter(Boolean);
  if (liens.length < 2) return;

  let courant = null;
  let attente = null;

  const relever = () => {
    attente = null;
    // Le rail est épinglé en mobile : une section est « lue » quand elle passe
    // sous lui, pas quand elle touche le haut de la fenêtre.
    const seuil = rail.getBoundingClientRect().height + 24;
    let actif = liens[0];
    liens.forEach((l) => {
      if (l.el.getBoundingClientRect().top <= seuil) actif = l;
    });
    if (actif.a === courant) return;
    courant = actif.a;
    liens.forEach(({ a }) => {
      const oui = a === courant;
      a.classList.toggle('is-active', oui);
      if (oui) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    });
  };

  const planifier = () => {
    if (attente) return;
    attente = requestAnimationFrame(relever);
  };

  window.addEventListener('scroll', planifier, { passive: true });
  window.addEventListener('resize', planifier, { passive: true });
  relever();
})();
